import React, { useState } from 'react';
import { StatusBadge, TrafficLight, resolveTrafficLight } from './ui/StatusBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';
import {
  Plus, Trash2, Package, FileText,
  CheckCircle, X, Edit2, Zap, ShieldCheck,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { revalidateContainer } from '../lib/api';

// Mobile Container Card Component
function ContainerCard({ container, statuses, isReadOnly, onUpdate, onDelete, selected, onToggleSelect }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(container);

  const handleSave = () => {
    onUpdate(container.id, {
      status_container_code: form.status_container_code,
      release_traffic_light: form.release_traffic_light,
      arrived_at: form.arrived_at,
      delivered_at: form.delivered_at,
      referencia_carga: form.referencia_carga,
      numero_pedimento: form.numero_pedimento,
      metodo_transporte_terrestre: form.metodo_transporte_terrestre,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid={`container-card-edit-${container.container_number}`}>
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-medium text-primary">{container.container_number}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={handleSave}>
              <CheckCircle size={14} className="text-emerald-400" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              <X size={14} />
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t('common.status')}</Label>
            <Select 
              value={form.status_container_code} 
              onValueChange={(v) => setForm({...form, status_container_code: v})}
            >
              <SelectTrigger className="h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map(s => (
                  <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('shipments.trafficLight')}</Label>
            <Select 
              value={form.release_traffic_light} 
              onValueChange={(v) => setForm({...form, release_traffic_light: v})}
            >
              <SelectTrigger className="h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NA">N/A</SelectItem>
                <SelectItem value="GREEN">{t('shipments.free')}</SelectItem>
                <SelectItem value="RED">{t('shipments.inspection')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('shipments.arrival')}</Label>
            <Input
              type="date"
              value={form.arrived_at?.slice(0, 10) || ''}
              onChange={(e) => setForm({...form, arrived_at: e.target.value ? e.target.value + 'T00:00:00' : ''})}
              className="h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">{t('shipments.delivery')}</Label>
            <Input
              type="date"
              value={form.delivered_at?.slice(0, 10) || ''}
              onChange={(e) => setForm({...form, delivered_at: e.target.value ? e.target.value + 'T00:00:00' : ''})}
              className="h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">{t('shipments.refCarga')}</Label>
            <Input
              value={form.referencia_carga || ''}
              onChange={(e) => setForm({...form, referencia_carga: e.target.value})}
              className="h-9 mt-1"
              placeholder={t('shipments.reference')}
            />
          </div>
          <div>
            <Label className="text-xs">{t('shipments.pedimento')}</Label>
            <Input
              value={form.numero_pedimento || ''}
              onChange={(e) => setForm({...form, numero_pedimento: e.target.value})}
              className="h-9 mt-1"
              placeholder={t('shipments.number')}
            />
          </div>
          <div>
            <Label className="text-xs">{t('shipments.transport')}</Label>
            <Select value={form.metodo_transporte_terrestre || 'pendiente'} onValueChange={(v) => setForm({...form, metodo_transporte_terrestre: v === 'pendiente' ? null : v})}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">{t('shipments.pending')}</SelectItem>
                <SelectItem value="tren">{t('shipments.train')} (Ferrovalle)</SelectItem>
                <SelectItem value="camion">{t('shipments.truck')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-card border rounded-sm p-3 ${selected ? 'border-primary bg-primary/5' : 'border-border'}`}
      data-testid={`container-card-${container.container_number}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <input 
              type="checkbox" 
              checked={selected}
              onChange={onToggleSelect}
              className="rounded border-border"
            />
          )}
          <span className="font-mono text-sm font-medium text-primary">{container.container_number}</span>
          {container.carrier_name && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: container.carrier_color + '20', color: container.carrier_color }}>
              {container.carrier_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge code={container.status_container_code} label={container.status_label} size="sm" />
          {!isReadOnly && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0"
              onClick={() => { setForm(container); setEditing(true); }}
              data-testid={`edit-container-mobile-${container.container_number}`}
            >
              <Edit2 size={14} />
            </Button>
          )}
        </div>
      </div>
      
      {/* Main Info */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
        <div className="text-muted-foreground">{t('shipments.trafficLight')}</div>
        <div><TrafficLight status={resolveTrafficLight(container)} size="sm" /></div>
        <div className="text-muted-foreground">{t('shipments.arrival')}</div>
        <div>{formatDate(container.arrived_at) || '\u2014'}</div>
        <div className="text-muted-foreground">{t('shipments.delivery')}</div>
        <div className="text-emerald-400">{formatDate(container.delivered_at) || '\u2014'}</div>
        {container.days_in_port_label && (
          <>
            <div className="text-muted-foreground">{t('shipments.daysInPort')}</div>
            <div className={container.delivered_at ? 'text-emerald-400' : 'text-amber-400'}>
              {container.days_in_port_label}
            </div>
          </>
        )}
      </div>
      
      {/* Expandable Details */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full text-xs text-muted-foreground flex items-center justify-center gap-1 py-1 hover:text-foreground"
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? t('common.viewLess') : t('common.viewMore')}
      </button>
      
      {expanded && (
        <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div className="text-muted-foreground">{t('shipments.refCarga')}</div>
          <div>{container.referencia_carga || '\u2014'}</div>
          <div className="text-muted-foreground">{t('shipments.pedimento')}</div>
          <div className="font-mono">{container.numero_pedimento || '\u2014'}</div>
          <div className="text-muted-foreground">{t('shipments.transport')}</div>
          <div>
            {container.metodo_transporte_terrestre === 'tren' ? (
              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium">{t('shipments.train')}</span>
            ) : container.metodo_transporte_terrestre === 'camion' ? (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">{t('shipments.truck')}</span>
            ) : '—'}
          </div>
          {!isReadOnly && (
            <div className="col-span-2 mt-2">
              <Button 
                size="sm" 
                variant="destructive" 
                className="w-full"
                onClick={() => onDelete(container.id)}
              >
                <Trash2 size={14} className="mr-1" />
                {t('common.delete')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Container Row Component (Desktop)
function ContainerRow({ container, statuses, isReadOnly, onUpdate, onDelete, onReload, selected, onToggleSelect }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(container);

  const handleSave = () => {
    onUpdate(container.id, {
      status_container_code: form.status_container_code,
      release_traffic_light: form.release_traffic_light,
      arrived_at: form.arrived_at,
      delivered_at: form.delivered_at,
      referencia_carga: form.referencia_carga,
      numero_pedimento: form.numero_pedimento,
      metodo_transporte_terrestre: form.metodo_transporte_terrestre,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-b border-border/50 bg-secondary/20">
        {!isReadOnly && <td className="py-2 px-3"></td>}
        <td className="py-2 px-3 font-mono text-sm">{container.container_number}</td>
        <td className="py-2 px-3"></td>
        <td className="py-2 px-3">
          <Select 
            value={form.status_container_code} 
            onValueChange={(v) => setForm({...form, status_container_code: v})}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(s => (
                <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="py-2 px-3">
          <Select 
            value={form.release_traffic_light} 
            onValueChange={(v) => setForm({...form, release_traffic_light: v})}
          >
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NA">N/A</SelectItem>
              <SelectItem value="GREEN">Libre</SelectItem>
              <SelectItem value="RED">Inspeccion</SelectItem>
            </SelectContent>
          </Select>
        </td>
        <td className="py-2 px-3">
          <Input
            type="datetime-local"
            value={form.arrived_at || ''}
            onChange={(e) => setForm({...form, arrived_at: e.target.value})}
            className="h-8"
          />
        </td>
        <td className="py-2 px-3">
          <Input
            type="datetime-local"
            value={form.delivered_at || ''}
            onChange={(e) => setForm({...form, delivered_at: e.target.value})}
            className="h-8"
          />
        </td>
        <td className="py-2 px-3">
          <Input
            value={form.referencia_carga || ''}
            onChange={(e) => setForm({...form, referencia_carga: e.target.value})}
            className="h-8"
          />
        </td>
        <td className="py-2 px-3">
          <Input
            value={form.numero_pedimento || ''}
            onChange={(e) => setForm({...form, numero_pedimento: e.target.value})}
            className="h-8 font-mono"
          />
        </td>
        <td className="py-2 px-3">
          <Select value={form.metodo_transporte_terrestre || 'pendiente'} onValueChange={(v) => setForm({...form, metodo_transporte_terrestre: v === 'pendiente' ? null : v})}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="tren">Tren</SelectItem>
              <SelectItem value="camion">Camion</SelectItem>
            </SelectContent>
          </Select>
        </td>
        <td className="py-2 px-3 text-sm text-muted-foreground">{container.days_in_port_label || '\u2014'}</td>
        <td className="py-2 px-3">
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={handleSave}>
              <CheckCircle size={14} className="text-emerald-500" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => setEditing(false)}>
              <X size={14} className="text-destructive" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr 
      className={`border-b border-border/50 hover:bg-secondary/10 ${selected ? 'bg-primary/5' : ''}`}
      data-testid={`container-row-${container.container_number}`}
    >
      {!isReadOnly && (
        <td className="py-2 px-3">
          <input 
            type="checkbox" 
            checked={selected}
            onChange={onToggleSelect}
            className="rounded border-border"
          />
        </td>
      )}
      <td className="py-2 px-3 font-mono text-sm">{container.container_number}</td>
      <td className="py-2 px-3">
        {container.carrier_name ? (
          <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ backgroundColor: container.carrier_color + '20', color: container.carrier_color }}>
            {container.carrier_name}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2 px-3">
        <StatusBadge code={container.status_container_code} label={container.status_label} size="sm" />
      </td>
      <td className="py-2 px-3">
        <TrafficLight status={resolveTrafficLight(container)} size="sm" />
      </td>
      <td className="py-2 px-3 text-sm text-muted-foreground">{formatDateTime(container.arrived_at) || '\u2014'}</td>
      <td className="py-2 px-3 text-sm text-emerald-400">{formatDateTime(container.delivered_at) || '\u2014'}</td>
      <td className="py-2 px-3 text-sm text-muted-foreground">{container.referencia_carga || '\u2014'}</td>
      <td className="py-2 px-3 text-sm font-mono text-muted-foreground">{container.numero_pedimento || '\u2014'}</td>
      <td className="py-2 px-3 text-sm">
        {container.metodo_transporte_terrestre === 'tren' ? (
          <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium">{t('shipments.train')}</span>
        ) : container.metodo_transporte_terrestre === 'camion' ? (
          <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">{t('shipments.truck')}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2 px-3 text-sm">
        {container.days_in_port_label ? (
          <span className={container.delivered_at ? 'text-emerald-400' : 'text-amber-400'}>
            {container.days_in_port_label}
          </span>
        ) : '\u2014'}
      </td>
      {!isReadOnly && (
        <td className="py-2 px-3">
          <div className="flex gap-1">
            <Button 
              size="icon" 
              variant="ghost" 
              onClick={() => { setForm(container); setEditing(true); }}
              title="Editar todo"
              data-testid={`edit-container-${container.container_number}`}
            >
              <FileText size={14} />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              onClick={async () => {
                const ok = window.confirm(`¿Revalidar ${container.container_number} contra la naviera? Esto eliminará datos de puerto/ferrovalle si el carrier indica que aún no ha arribado.`);
                if (!ok) return;
                const tid = toast.loading('Revalidando con carrier...');
                try {
                  const res = await revalidateContainer(container.container_number);
                  const data = res?.data || {};
                  toast.success(
                    `Revalidado: carrier=${data.carrier_status || 'sin datos'}, purgados puerto=${data.deleted_port_records ?? 0}, ferrovalle=${data.deleted_ferrovalle_records ?? 0}`,
                    { id: tid, duration: 6000 }
                  );
                  if (onReload) await onReload();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Error al revalidar', { id: tid });
                }
              }}
              title="Revalidar contra carrier — solo emergencia (la revalidación automática corre diariamente)"
              data-testid={`revalidate-container-${container.container_number}`}
            >
              <ShieldCheck size={14} className="text-blue-400" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              onClick={() => onDelete(container.id)}
              className="text-destructive hover:text-destructive"
              title="Eliminar"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
}

// Main ContainerTable component - the full containers tab content
export function ContainerTable({
  containers,
  containerStatuses,
  isReadOnly,
  isMobile,
  onUpdateContainer,
  onDeleteContainer,
  onAddContainersBulk,
  onReloadData,
}) {
  const { t } = useI18n();
  const [showAddContainer, setShowAddContainer] = useState(false);
  const [bulkContainers, setBulkContainers] = useState('');
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    status_container_code: '',
    arrived_at: '',
    delivered_at: '',
  });

  const handleAddBulk = () => {
    const lines = bulkContainers.split('\n').filter(l => l.trim());
    if (lines.length === 0) return;
    onAddContainersBulk(lines.map(l => l.trim()));
    setBulkContainers('');
    setShowAddContainer(false);
  };

  const handleBulkEdit = async () => {
    if (selectedContainers.length === 0) return;

    const updateData = {};
    if (bulkEditForm.status_container_code) updateData.status_container_code = bulkEditForm.status_container_code;
    if (bulkEditForm.arrived_at) updateData.arrived_at = bulkEditForm.arrived_at;
    if (bulkEditForm.delivered_at) updateData.delivered_at = bulkEditForm.delivered_at;

    if (Object.keys(updateData).length === 0) {
      toast.error(t('common.selectAtLeastOneField'));
      return;
    }

    for (const containerId of selectedContainers) {
      await onUpdateContainer(containerId, updateData, true);
    }

    if (onReloadData) await onReloadData();

    toast.success(`${selectedContainers.length} ${t('shipments.updated')}`);
    setSelectedContainers([]);
    setShowBulkEdit(false);
    setBulkEditForm({ status_container_code: '', arrived_at: '', delivered_at: '' });
  };

  const toggleContainerSelection = (containerId) => {
    setSelectedContainers(prev =>
      prev.includes(containerId)
        ? prev.filter(id => id !== containerId)
        : [...prev, containerId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContainers.length === containers.length) {
      setSelectedContainers([]);
    } else {
      setSelectedContainers(containers.map(c => c.id));
    }
  };

  return (
    <div className="space-y-4">
      {!isReadOnly && (
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg">{t('shipments.containers')}</h3>
          <Dialog open={showAddContainer} onOpenChange={setShowAddContainer}>
            <DialogTrigger asChild>
              <Button data-testid="add-container-btn">
                <Plus size={16} className="mr-2" />
                {isMobile ? t('common.add') : t('shipments.addContainers')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('shipments.addContainers')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('shipments.enterContainerNumbers')}:
                </p>
                <Textarea
                  value={bulkContainers}
                  onChange={(e) => setBulkContainers(e.target.value)}
                  placeholder={"MSCU1234567\nTCLU7654321\nCMAU9876543"}
                  rows={6}
                  className="font-mono"
                  data-testid="bulk-containers-input"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddContainer(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleAddBulk} data-testid="add-containers-submit">
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {containers.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <Package className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('shipments.noContainers')}</p>
        </div>
      ) : isMobile ? (
        /* Mobile Cards View */
        <div className="space-y-3" data-testid="mobile-containers-list">
          {containers.map((container) => (
            <ContainerCard
              key={container.id}
              container={container}
              statuses={containerStatuses}
              isReadOnly={isReadOnly}
              onUpdate={(id, data) => onUpdateContainer(id, data)}
              onDelete={onDeleteContainer}
              selected={selectedContainers.includes(container.id)}
              onToggleSelect={() => toggleContainerSelection(container.id)}
            />
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          {/* Bulk Edit Bar */}
          {!isReadOnly && selectedContainers.length > 0 && (
            <div className="bg-primary/10 border-b border-primary/20 p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-primary">
                {selectedContainers.length} {t('shipments.bulkSelect')}
              </span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setSelectedContainers([])}
                >
                  {t('shipments.deselect')}
                </Button>
                <Popover open={showBulkEdit} onOpenChange={setShowBulkEdit}>
                  <PopoverTrigger asChild>
                    <Button size="sm" data-testid="bulk-edit-btn">
                      <Zap size={14} className="mr-2" />
                      {t('shipments.quickEdit')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">{t('common.edit')} {selectedContainers.length} {t('shipments.containers').toLowerCase()}</h4>
                      <p className="text-xs text-muted-foreground">{t('shipments.onlyFilledFields')}</p>
                      <div className="space-y-2">
                        <Label className="text-xs">{t('common.status')}</Label>
                        <Select 
                          value={bulkEditForm.status_container_code} 
                          onValueChange={(v) => setBulkEditForm({...bulkEditForm, status_container_code: v})}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder={t('shipments.noChange')} />
                          </SelectTrigger>
                          <SelectContent>
                            {containerStatuses.map(s => (
                              <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">{t('shipments.arrivalDate')}</Label>
                        <Input
                          type="datetime-local"
                          value={bulkEditForm.arrived_at || ''}
                          onChange={(e) => setBulkEditForm({...bulkEditForm, arrived_at: e.target.value})}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">{t('shipments.deliveryDate')}</Label>
                        <Input
                          type="datetime-local"
                          value={bulkEditForm.delivered_at || ''}
                          onChange={(e) => setBulkEditForm({...bulkEditForm, delivered_at: e.target.value})}
                          className="h-8"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" variant="outline" onClick={() => setShowBulkEdit(false)} className="flex-1">
                          {t('common.cancel')}
                        </Button>
                        <Button size="sm" onClick={handleBulkEdit} className="flex-1">
                          {t('shipments.applyTo')} {selectedContainers.length}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
          <table className="w-full dense-table min-w-[900px]">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                {!isReadOnly && (
                  <th className="py-2 px-3 w-10">
                    <input 
                      type="checkbox" 
                      checked={selectedContainers.length === containers.length && containers.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                )}
                <th className="py-2 px-3 font-medium">{t('shipments.containerNumber')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.carrier')}</th>
                <th className="py-2 px-3 font-medium">{t('common.status')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.trafficLight')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.arrival')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.delivery')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.refCarga')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.pedimento')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.transport')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.daysInPort')}</th>
                {!isReadOnly && <th className="py-2 px-3 font-medium w-20">{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {containers.map((container) => (
                <ContainerRow
                  key={container.id}
                  container={container}
                  statuses={containerStatuses}
                  isReadOnly={isReadOnly}
                  onUpdate={(id, data) => onUpdateContainer(id, data)}
                  onDelete={onDeleteContainer}
                  onReload={onReloadData}
                  selected={selectedContainers.includes(container.id)}
                  onToggleSelect={() => toggleContainerSelection(container.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
